import React, { useState, useRef } from "react"
import { CustomModal } from "./modal"
import { useMutation } from "react-query"
import { uploadProofOfPayment } from "../services/expert"
import { Button } from "./button"
import * as Yup from "yup"
import { Formik, Form } from "formik"

function AddProofOfPaymentModal({ transaction, onClose, onSuccess }) {
    const isOpen = Boolean(transaction)
    const [selectedFile, setSelectedFile] = useState(null)
    const [fileName, setFileName] = useState('')
    const fileInputRef = useRef(null)

    const { mutate, isLoading } = useMutation((fileData) => uploadProofOfPayment(transaction.id, fileData), {
        onSuccess: () => {
            alert("Proof of payment uploaded successfully!")
            onSuccess()
            onClose()
            // Reset form
            setSelectedFile(null)
            setFileName('')
        },
        onError: e => {
            alert(e.data?.message || 'Failed to upload proof of payment')
        }
    })

    const handleFileSelect = (e) => {
        const file = e.target.files[0]
        if (file) {
            // Validate file type
            const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'application/pdf']
            if (!allowedTypes.includes(file.type)) {
                alert('Please select a valid file type (JPEG, PNG, GIF, or PDF)')
                return
            }

            // Validate file size (10MB max)
            if (file.size > 10 * 1024 * 1024) {
                alert('File size must be less than 10MB')
                return
            }

            setSelectedFile(file)
            setFileName(file.name)
        }
    }

    const handleFileButtonClick = () => {
        fileInputRef.current?.click()
    }

    const validateForm = () => {
        if (!selectedFile) {
            alert('Please select a file to upload')
            return false
        }
        return true
    }

    return (
        <CustomModal
            open={isOpen}
            onClose={onClose}
            className="commonCustomModal feedbackModal"
            body={transaction ? (
                <Formik
                    initialValues={{
                        proof_file: null,
                    }}
                    validationSchema={Yup.object().shape({
                        // File validation is handled in handleFileSelect and validateForm
                    })}
                    validateOnChange={false}
                    validateOnBlur={false}
                    onSubmit={(values, { setSubmitting }) => {
                        if (validateForm()) {
                            mutate(selectedFile)
                        }
                        setSubmitting(false);
                    }}
                >
                    {({ errors, touched }) => (
                        <Form className="form mt-30">
                            {transaction.proof_of_payment_file_url && (
                                <div className="mb-20">
                                    <div className="is-flex aic">
                                        <span className="fw-600 mr-10">📎 Current Proof:</span>
                                        <a
                                            href={transaction.proof_of_payment_file_url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="color-blue text-decoration-none"
                                            style={{ wordBreak: 'break-all' }}
                                        >
                                            View Current Proof of Payment
                                        </a>
                                    </div>
                                </div>
                            )}

                            <div className="mb-30">
                                <label htmlFor="proof_file">Proof of Payment File *</label>
                                <input
                                    type="file"
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    accept=".jpg,.jpeg,.png,.gif,.pdf"
                                    style={{ display: 'none' }}
                                />
                                <div className="is-flex fdr aic">
                                    <Button
                                        text='Choose File'
                                        onClick={handleFileButtonClick}
                                        className='secondary bordered btn-md mr-10'
                                        type='button'
                                    />
                                    <div className="file-name">
                                        {fileName || 'No file selected'}
                                    </div>
                                </div>
                                <small className="form-hint">
                                    Accepted formats: JPEG, PNG, GIF, PDF. Max size: 10MB
                                </small>
                                {errors.proof_file && touched.proof_file ? (
                                    <span className="v-error">{errors.proof_file}</span>
                                ) : null}
                            </div>

                            <div className="is-flex aic jcfe">
                                <Button
                                    text='Cancel'
                                    onClick={onClose}
                                    className='secondary bordered btn-md mr-10'
                                    disabled={isLoading}
                                />
                                <Button
                                    text='Upload Proof'
                                    type='submit'
                                    className='primary btn-md'
                                    disabled={isLoading || !selectedFile}
                                />
                            </div>
                        </Form>
                    )}
                </Formik>
            ) : "Loading..."}
        />
    )
}

export { AddProofOfPaymentModal }
